import Alpine from 'alpinejs';
import axios from 'axios';
import {getLocation, getLocationSupport} from '../../../assets/scripts/utils/geolocation';
import debounce from '../../../assets/scripts/utils/debounce';
import decodeHTMLEntities from '../../../assets/scripts/utils/decode';
import position from '../../../assets/scripts/utils/position';
import {screens} from '../../../../config/breakpoints.js';

const { phone_only, tablet, desktop, large_desktop, extra_large_desktop } = screens;
const mqlPhoneOnly = window.matchMedia(`(max-width: ${parseFloat(phone_only.max, 10)}px)`);
const mqlTablet = window.matchMedia(`(min-width: ${parseInt(tablet, 10)}px)`);
const mqlDesktop = window.matchMedia(`(min-width: ${parseInt(desktop, 10)}px)`);
const mqlLargeDesktop = window.matchMedia(`(min-width: ${parseInt(large_desktop, 10)}px)`);
const mqlExtraLargeDesktop = window.matchMedia(`(min-width: ${parseInt(extra_large_desktop, 10)}px)`);

Alpine.data('job_search', () => ({
  // ---
  // SEARCH FORM
  // ___
  geolocation_supported: getLocationSupport(),
  distance_open: false,
  country_value: 'de',

  location_value: '',
  location_label: '',
  location_data: [],
  location_focused: false,
  location_selected: false,

  distance_value: null,
  distance_label: null,
  distance_selected: false,
  distance_loading: false,

  keyword_value: '',
  keyword_data: [],
  keyword_focused: false,
  keyword_loading: false,
  keyword_selected: false,

  // ---
  // FILTER
  // ___
  filter_field_open: false,
  filter_field_value: '',
  filter_field_label: '',

  filter_employment_type_open: false,
  filter_employment_type_value: '',
  filter_employment_type_label: '',

  filter_sorting_value: '',

  desktop_size: false,

  preview_open: false,

  page: 0,

  jobs: [],
  jobs_count: 0,

  selected_job: '',

  showNoResults: false,
  clonedBackButton: null,

  init() {
    const self = this;
    Alpine.nextTick(() => {

        self.loadJobs();
        self.mountFilters();
        self.detectBreakpoints();

    });
  },

  setCountry(value) {
    const self = this;
    self.country_value = value;
    self.location_focused = false;
    self.location_data = [];
    self.setBodyScrollability(true);
  },

  async getCoords() {
    try {
      const location = await getLocation();
      if (!location) {
        return null;
      }
      const { longitude, latitude } = location.coords;
      return `${longitude},${latitude}`;
    } catch (error) {
      console.warn(error);
      return null;
    }
  },

  setKeyword(value) {
    const self = this;
    self.keyword_value = value;
    self.keyword_data = [];
    self.keyword_selected = true;
    self.keyword_focused = false;
    self.setBodyScrollability(true);
  },

  handleKeywordFocus() {
    const self = this;
    self.location_focused = false;
    self.distance_open = false;
    self.setBodyScrollability(false);
    self.keyword_focused = true;
  },

  handleKeywordFocusOut(event) {
    const self = this;
    if (!self.$refs.keyword_input.contains(event.relatedTarget)) {
      self.keyword_focused = false;
      self.setBodyScrollability(true);
    }
  },

  searchKeywords(input) {
    const self = this;
    const { value } = input;
    const { searchUrl } = input.dataset;
    self.keyword_selected = false;
    axios
      .get(searchUrl, {
        params: { keyword: value , country: self.country_value },
      })
      .then(response => {
        const { full_name } = response.data;
        self.keyword_data = full_name;
        self.location_focused = false;
        self.location_data = [];
      });
  },

  handleLocationFocus(event) {
    const self = this;
    self.keyword_focused = false;
    self.location_focused = true;
    self.distance_open = false;
    self.keyword_data = [];
    self.setBodyScrollability(false);
  },

  handleLocationFocusOut(event) {
    const self = this;
    if (!self.$refs.location_input.contains(event.relatedTarget)) {
      self.location_focused = false;
      self.setBodyScrollability(true);
    }
  },

  selectLocation(location) {
    const self = this;
    const [label, value] = location;
    self.location_value = value || '';
    self.location_label = label;
    self.location_data = [];
    self.location_selected = true;
    self.location_focused = false;
  },

  async selectCurrentLocation(event) {
    const self = this;
    const coords = await self.getCoords();
    const clickTarget = event.target.innerText ? event.target : event.target.closest('div');
    const text = clickTarget.innerText.trim();
    if (coords) {
      self.selectLocation([text, coords]);
    }
    self.$refs.job_search_form.focus();
    self.location_selected = true;
    self.location_focused = false;
  },

  searchLocation(input) {
    const self = this;
    const { value, dataset } = input;
    const { searchUrl } = dataset;
    self.location_value = value;
    self.distance_open = false;
    axios
      .get(searchUrl, {
        params: { location: value, country: self.country_value },
      })
      .then(response => {
        self.location_data = response.data;
      });
  },

  toggleDistanceOpen: debounce(function (event) {
    const self = this;
    self.distance_open = !self.distance_open;
    self.location_focused = false;
    self.location_data = [];
    self.keyword_data = [];
    self.setBodyScrollability(true);
  }, 20),

  selectDistance(valueArr) {
    const self = this;
    const [value, label] = valueArr;
    const fallbackValue = 50;
    if (!Number.isInteger(value)) {
      self.distance_value = fallbackValue;
      self.distance_label = `${fallbackValue} km`;
    } else {
      self.distance_value = value;
      self.distance_label = label;
    }
    self.distance_open = false;
  },

  mountComponent() {
    const self = this;
    const firstResult = self.$refs.search_results?.querySelector('.job-search-results-entry');

    if (!firstResult) {
      return;
    }

    if (mqlDesktop.matches) {
      self.generatePreview(firstResult);
    }

    mqlDesktop.onchange = event => {
      if (event.matches) {
        self.generatePreview(firstResult);
      }
    };
  },

  mountFilters() {
    document.addEventListener('alpine:initialized', () => {
      const self = this;
      [
        ['filter_field_value', 'filter_field_label', 'filter_field_open'],
        ['filter_employment_type_value', 'filter_employment_type_label', 'filter_employment_type_open'],
      ].forEach(([value, label, open]) => {
        const list = self.$refs.search_filters?.querySelector(`[x-show="${open}"]`);
        const listItem = list?.querySelector(`[data-value="${self[value]}"]`);
        self[label] = listItem?.innerHTML.replace(/\(\d{1,5}\)/gi, '').trim() || '';
      });
    });
  },

  unsetField(fieldname) {
    const self = this;
    self[`${fieldname}_selected`] = false;
    self[`${fieldname}_value`] = '';
    self[`${fieldname}_label`] = '';
    self[`${fieldname}_data`] = [];
    if (typeof self[`${fieldname}_focused`] !== undefined) {
      self[`${fieldname}_focused`] = false;
    }
  },

  onBeforeSubmit(event) {
    event.preventDefault();
    const self = this;
    if (self.location_value === self.location_label) {
      self.distance_value = '';
      self.distance_label = '';
    }
    return false;
  },

  loadJobs(scrollBy = 0) {
    const self = this;
    const form = self.$refs.job_search_form || document.querySelector('form.job-search-form');
    const submitUrl = form.getAttribute('data-jobs-url');
    const params = {};
    Array.from(form.elements).forEach(el => (el.name ? (params[el.name] = el.value) : ''));
    axios.get(submitUrl, { params }).then(response => {
      self.jobs_count = response.data.total;
      if (self.page === 0) {
        self.jobs = Object.values(response.data.jobs);
      } else {
        self.jobs = self.jobs.concat(Object.values(response.data.jobs));
      }
      if (response.data.total === 0) {
        self.showNoResults = true;
      } else {
        self.showNoResults = false;
        Alpine.nextTick(() => {
          if (!self.$refs.search_results?.querySelector('.entry-active')) {
            self.mountComponent();
          }
          if (self.page > 0 && scrollBy > 0) {
            self.$refs.job_results.scrollBy({ top: scrollBy, behavior: 'smooth' });
          }
        });
      }
    });
    return false;
  },

  // Filter
  openFilterField(element) {
    const self = this;
    let previousSibling = element.closest('.select-wrapper')?.querySelector('.avanti-icon-dropdown');
    if (previousSibling) {
      previousSibling.classList.add('open');
    }
    self.filter_field_open = true;
    self.filter_employment_type_open = false;
  },

  openEmploymentTypeField(element) {
    const self = this;
    let previousSibling = element.closest('.select-wrapper')?.querySelector('.avanti-icon-dropdown');
    if (previousSibling) {
      previousSibling.classList.add('open');
    }
    self.filter_field_open = false;
    self.filter_employment_type_open = true;
  },

  selectField(element) {
    const self = this;
    const { value } = element.dataset;
    const label = element.innerHTML.replace(/\(\d{1,5}\)/gi, '').trim();
    self.filter_field_value = value;
    self.filter_field_label = decodeHTMLEntities(label);
    self.filter_field_open = false;
    self.page = 0;
    self.closePreview();
    Alpine.nextTick(() => self.loadJobs());
  },

  selectEmploymentType(element) {
    const self = this;
    const { value } = element.dataset;
    const label = element.innerHTML.replace(/\(\d{1,5}\)/gi, '').trim();
    self.filter_employment_type_value = value;
    self.filter_employment_type_label = decodeHTMLEntities(label);
    self.filter_employment_type_open = false;
    self.page = 0;
    self.closePreview();
    Alpine.nextTick(() => self.loadJobs());
  },

  setSortBy(criteria) {
    const self = this;
    self.filter_sorting_value = criteria;
    self.page = 0;
    self.closePreview();
    Alpine.nextTick(() => self.loadJobs());
  },

  resetFilters() {
    const self = this;
    self.filter_field_value = '';
    self.filter_employment_type_value = '';
    self.page = 0;
    self.closePreview();
    Alpine.nextTick(() => self.loadJobs());
  },

  generatePreview(element) {
    const self = this;
    const activeEntryClass = 'entry-active';
    const currentPosition = position(element);
    element.dataset.position = JSON.stringify(currentPosition);
    Array.from(self.$refs.search_results.querySelectorAll('.job-search-results-entry')).forEach(elm => {
      elm.classList.remove(activeEntryClass);
    });
    self.preview_open = true;
    element.classList.add(activeEntryClass);
    const jobDetailsUrl = element.getAttribute('data-preview-url');
    const previewWrapper = self.$refs.job_preview;
    axios.get(jobDetailsUrl).then(response => {
      previewWrapper.innerHTML = response.data;
      if (self.desktop_size) {
        previewWrapper.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        document.querySelector('.job-search-preview-col').scrollIntoView({ behavior: 'smooth' });
      }
    });
  },

  closePreview() {
    const self = this;
    const activeEntryClass = 'entry-active';
    self.preview_open = false;
    Array.from(self.$refs.search_results.querySelectorAll('.job-search-results-entry')).forEach(elm => {
      elm.classList.remove(activeEntryClass);
    });
    const preview = self.$refs.job_preview;
    if (self.desktop_size) {
      preview.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (self.clonedBackButton) {
      console.log('closePreview');
      self.clonedBackButton.remove();
      self.clonedBackButton = null;
    }
  },

  detectBreakpoints() {
    const self = this;
    self.phone_only_size = mqlPhoneOnly.matches;
    self.tablet_size = mqlTablet.matches;
    self.desktop_size = mqlDesktop.matches;
    self.large_desktop_size = mqlLargeDesktop.matches;
    self.extra_large_desktop_size = mqlExtraLargeDesktop.matches;

    mqlPhoneOnly.onchange = event => {
      self.phone_only_size = event.matches;
      if (!event.matches) {
        self.setBodyScrollability(true);
      }
    };

    mqlTablet.onchange = event => {
      self.tablet_size = event.matches;
    };

    mqlDesktop.onchange = event => {
      self.desktop_size = event.matches;
    };

    mqlLargeDesktop.onchange = event => {
      self.large_desktop_size = event.matches;
    };

    mqlExtraLargeDesktop.onchange = event => {
      self.extra_large_desktop_size = event.matches;
    };
  },

  loadMore() {
    const self = this;
    self.page = self.page + 1;
    const formPositionBottom = self.$refs.job_search_component.querySelector('.job-search').getBoundingClientRect().bottom;
    const loadMorePositionTop = self.$refs.search_results.querySelector('.job-search-results-load-more').getBoundingClientRect().top;
    Alpine.nextTick(() => self.loadJobs(loadMorePositionTop - formPositionBottom));
  },

  setBodyScrollability(scrollabilityBool = false) {
    const self = this;
    const scrollPreventingClass = 'job-search-mobile-overlay';

    if (scrollabilityBool) {
      document.body.classList.remove(scrollPreventingClass);
    } else {
      if (self.phone_only_size) {
        document.body.classList.add(scrollPreventingClass);
      }
    }
  },
}));
